export const BADGE_NOTIFICATION_TYPE = {
    SUB_TIER_UP: "sub_tier_up",
    XP_EARNED: "xp_earned",
    ACHIEVEMENT_UNLOCKED: "achievement_unlocked",
} as const;

export type BadgeNotificationType =
    typeof BADGE_NOTIFICATION_TYPE[keyof typeof BADGE_NOTIFICATION_TYPE];

const SUPPORTED_BADGE_NOTIFICATION_TYPES = new Set<string>(
    Object.values(BADGE_NOTIFICATION_TYPE),
);

/**
 * True only when the notification's `metadata.type` maps to a view we can
 * actually render. Items with a missing, unknown, or future type would
 * otherwise open the popup and occupy the auto-dismiss window with an empty
 * card, so they must be filtered out before they reach the queue.
 */
export function isSupportedBadgeNotification(
    notification: BadgeNotification,
): boolean {
    const type = notification.metadata?.type;
    return type != null && SUPPORTED_BADGE_NOTIFICATION_TYPES.has(type);
}

export type SubTierUpMetadata = {
    type: typeof BADGE_NOTIFICATION_TYPE.SUB_TIER_UP;
    tier?: number;
    new_sub_tier?: number;
};

export type XpBreakdownComponent = {
    title: string;
    score: number;
};

export type XpBreakdown = {
    components: XpBreakdownComponent[];
    total: number;
    allowed: number;
};

export type XpEarnedMetadata = {
    type: typeof BADGE_NOTIFICATION_TYPE.XP_EARNED;
    breakdown?: XpBreakdown;
    display_xp?: number;
    total_xp?: number;
};

export type AchievementUnlockedMetadata = {
    type: typeof BADGE_NOTIFICATION_TYPE.ACHIEVEMENT_UNLOCKED;
    id?: string;
    name: string;
    description?: string;
    iconUrl: string;
    category?: string;
};

export type BadgeNotificationMetadata =
    | SubTierUpMetadata
    | XpEarnedMetadata
    | AchievementUnlockedMetadata;

export type BadgeNotification = {
    id?: string;
    title?: string;
    description?: string;
    metadata?: BadgeNotificationMetadata;
};

const TIMESTAMP_FIELDS = [
    "created_at",
    "createdAt",
    "earned_at",
    "earnedAt",
    "timestamp",
] as const;

function extractTimestamp(notification: BadgeNotification): string | undefined {
    // Timestamps aren't part of the typed metadata union, so read them loosely.
    const metadata = notification.metadata as Record<string, unknown> | undefined;
    for (const field of TIMESTAMP_FIELDS) {
        const value = metadata?.[field];
        if (value != null && value !== "") {
            return String(value);
        }
    }
    return undefined;
}

/**
 * Derives a stable identity for a notification, independent of its full
 * payload shape. Both id assignment and dedupe keying rely on this so they can
 * never disagree about what makes two notifications "the same".
 *
 * Priority: server top-level id > a stable id inside metadata (e.g. an
 * achievement id) > a metadata timestamp. Returns undefined only when the
 * notification carries nothing identifiable.
 */
function deriveStableId(notification: BadgeNotification): string | undefined {
    if (notification.id != null && notification.id !== "") {
        return String(notification.id);
    }

    const metadata = notification.metadata as Record<string, unknown> | undefined;
    const metadataId = metadata?.id;
    if (metadataId != null && metadataId !== "") {
        return String(metadataId);
    }

    return extractTimestamp(notification);
}

function toNotificationArray(input: unknown): BadgeNotification[] {
    if (Array.isArray(input)) return input as BadgeNotification[];
    // The poll endpoint sometimes wraps the list (e.g. { notifications: [...] }).
    if (input && typeof input === "object") {
        const wrapped = (input as Record<string, unknown>).notifications;
        if (Array.isArray(wrapped)) return wrapped as BadgeNotification[];
    }
    return [];
}

/**
 * Ensures each notification has a unique `id`.
 * Uses the derived stable id (see `deriveStableId`) when one exists, appending a
 * sequence suffix when several notifications in the same poll share that id.
 */
export function assignBadgeNotificationIds(
    input: unknown,
): BadgeNotification[] {
    const notifications = toNotificationArray(input);
    const seqById = new Map<string, number>();

    return notifications.map((notification) => {
        if (notification.id != null && notification.id !== "") {
            return notification;
        }

        const baseId = deriveStableId(notification);
        if (!baseId) return notification;

        const seq = seqById.get(baseId) ?? 0;
        seqById.set(baseId, seq + 1);

        const id = seq === 0 ? baseId : `${baseId}-${seq}`;
        return { ...notification, id };
    });
}

export function getBadgeNotificationKey(notification: BadgeNotification): string {
    const id = deriveStableId(notification);
    if (id) return id;

    // Last resort only: nothing identifiable on the notification at all.
    // Serializing keeps identical payloads from replaying, at the (now rare)
    // cost of a possible replay if the API shape shifts for this one case.
    return JSON.stringify(notification);
}
